'use client';

import { useEffect, useRef, useState } from 'react';
import { backupSummary, createBackupPayload, restoreBackupPayload, validateImportPayload } from '@/lib/backup';
import { storageErrorMessage } from '@/lib/storageErrors';

const GOOGLE_CLIENT_ID = process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID ?? '';
const DRIVE_SCOPE = 'https://www.googleapis.com/auth/drive.appdata';
const BACKUP_FILE_NAME = 'licaio-backup.json';
const LAST_BACKUP_KEY = 'licaio-drive-last-backup';

type DriveFile = {
  id: string;
  name: string;
  modifiedTime?: string;
  size?: string;
};

type DriveListResponse = {
  files?: DriveFile[];
};

type GoogleTokenResponse = {
  access_token?: string;
  error?: string;
  error_description?: string;
};

type TokenClient = {
  requestAccessToken: (overrideConfig?: { prompt?: '' | 'consent' | 'select_account' }) => void;
};

declare global {
  interface Window {
    google?: {
      accounts: {
        oauth2: {
          initTokenClient: (config: {
            client_id: string;
            scope: string;
            callback: (response: GoogleTokenResponse) => void;
            include_granted_scopes?: boolean;
          }) => TokenClient;
        };
      };
    };
  }
}

let googleIdentityPromise: Promise<void> | null = null;

function notifyUpdate() {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event('budget-db-update'));
  }
}

function loadGoogleIdentityScript() {
  if (window.google?.accounts?.oauth2) {
    return Promise.resolve();
  }

  if (googleIdentityPromise) {
    return googleIdentityPromise;
  }

  googleIdentityPromise = new Promise((resolve, reject) => {
    const existingScript = document.getElementById('google-identity-services');
    if (existingScript) {
      existingScript.addEventListener('load', () => resolve(), { once: true });
      existingScript.addEventListener('error', () => reject(new Error('Impossible de charger Google Identity Services.')), { once: true });
      return;
    }

    const script = document.createElement('script');
    script.id = 'google-identity-services';
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Impossible de charger Google Identity Services.'));
    document.head.appendChild(script);
  });

  return googleIdentityPromise;
}

async function requestAccessToken(prompt: '' | 'consent' | 'select_account') {
  if (!GOOGLE_CLIENT_ID) {
    throw new Error('Ajoutez NEXT_PUBLIC_GOOGLE_CLIENT_ID dans les variables d’environnement pour activer Google Drive.');
  }

  await loadGoogleIdentityScript();

  return new Promise<string>((resolve, reject) => {
    const tokenClient = window.google?.accounts.oauth2.initTokenClient({
      client_id: GOOGLE_CLIENT_ID,
      scope: DRIVE_SCOPE,
      include_granted_scopes: true,
      callback: (response) => {
        if (response.error || !response.access_token) {
          reject(new Error(response.error_description || 'Connexion Google annulée ou refusée.'));
          return;
        }
        resolve(response.access_token);
      },
    });

    if (!tokenClient) {
      reject(new Error('Google Identity Services est indisponible.'));
      return;
    }

    tokenClient.requestAccessToken({ prompt });
  });
}

async function driveFetch<T>(url: string, accessToken: string, options: RequestInit = {}): Promise<T> {
  const headers = new Headers(options.headers);
  headers.set('Authorization', `Bearer ${accessToken}`);

  const response = await fetch(url, { ...options, headers });

  if (!response.ok) {
    if (response.status === 401) {
      throw new Error('Session Google expirée. Reconnectez Google Drive puis réessayez.');
    }
    const detail = await response.text();
    throw new Error(detail || `Erreur Google Drive (${response.status}).`);
  }

  return response.json() as Promise<T>;
}

async function findBackupFile(accessToken: string) {
  const params = new URLSearchParams({
    spaces: 'appDataFolder',
    q: `name='${BACKUP_FILE_NAME.replace(/'/g, "\\'")}'`,
    fields: 'files(id,name,modifiedTime,size)',
    pageSize: '10',
  });
  const result = await driveFetch<DriveListResponse>(`https://www.googleapis.com/drive/v3/files?${params.toString()}`, accessToken);
  return result.files?.[0] ?? null;
}

async function createDriveFile(accessToken: string, payload: unknown) {
  const boundary = `licaio_${Date.now()}`;
  const metadata = JSON.stringify({ name: BACKUP_FILE_NAME, parents: ['appDataFolder'] });
  const content = JSON.stringify(payload, null, 2);
  const body = new Blob(
    [
      `--${boundary}\r\n`,
      'Content-Type: application/json; charset=UTF-8\r\n\r\n',
      metadata,
      `\r\n--${boundary}\r\n`,
      'Content-Type: application/json; charset=UTF-8\r\n\r\n',
      content,
      `\r\n--${boundary}--`,
    ],
    { type: `multipart/related; boundary=${boundary}` }
  );

  return driveFetch<DriveFile>('https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart&fields=id,name,modifiedTime,size', accessToken, {
    method: 'POST',
    headers: { 'Content-Type': body.type },
    body,
  });
}

async function updateDriveFile(accessToken: string, fileId: string, payload: unknown) {
  return driveFetch<DriveFile>(`https://www.googleapis.com/upload/drive/v3/files/${fileId}?uploadType=media&fields=id,name,modifiedTime,size`, accessToken, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json; charset=UTF-8' },
    body: JSON.stringify(payload, null, 2),
  });
}

async function downloadDriveBackup(accessToken: string, fileId: string) {
  return driveFetch<unknown>(`https://www.googleapis.com/drive/v3/files/${fileId}?alt=media`, accessToken);
}

function formatBackupTime(value?: string) {
  if (!value) return 'Aucune sauvegarde Drive trouvée';
  return new Intl.DateTimeFormat('fr-FR', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(value));
}

export default function GoogleDriveBackupPanel() {
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [lastBackup, setLastBackup] = useState<DriveFile | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [autoEnabled, setAutoEnabled] = useState(false);
  const [pendingRestore, setPendingRestore] = useState(false);
  const [busy, setBusy] = useState<'connect' | 'save' | 'restore' | 'check' | null>(null);
  const autoTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const raw = localStorage.getItem(LAST_BACKUP_KEY);
    if (!raw) return;
    try {
      setLastBackup(JSON.parse(raw) as DriveFile);
    } catch {
      localStorage.removeItem(LAST_BACKUP_KEY);
    }
  }, []);

  const rememberBackup = (file: DriveFile | null) => {
    setLastBackup(file);
    if (file) {
      localStorage.setItem(LAST_BACKUP_KEY, JSON.stringify(file));
    }
  };

  const connectDrive = async () => {
    setBusy('connect');
    setMessage(null);
    try {
      const token = await requestAccessToken('consent');
      setAccessToken(token);
      setAutoEnabled(true);
      const file = await findBackupFile(token);
      rememberBackup(file);
      setMessage(file ? 'Google Drive est connecté. Sauvegarde automatique active tant que l’app reste ouverte.' : 'Google Drive est connecté. Lancez une première sauvegarde.');
    } catch (err) {
      setMessage(err instanceof Error ? err.message : 'Impossible de connecter Google Drive.');
    } finally {
      setBusy(null);
    }
  };

  const ensureAccessToken = async () => {
    if (accessToken) return accessToken;
    const token = await requestAccessToken('consent');
    setAccessToken(token);
    return token;
  };

  const saveToDrive = async (silent = false) => {
    const token = await ensureAccessToken();
    if (!silent) {
      setBusy('save');
      setMessage(null);
    }

    try {
      const payload = await createBackupPayload();
      const existingFile = await findBackupFile(token);
      const file = existingFile ? await updateDriveFile(token, existingFile.id, payload) : await createDriveFile(token, payload);
      rememberBackup(file);
      if (!silent) {
        setAutoEnabled(true);
        setMessage('Sauvegarde envoyée dans Google Drive.');
      }
    } catch (err) {
      if (!silent) {
        setMessage(storageErrorMessage('Impossible de sauvegarder dans Google Drive.', err));
      }
      throw err;
    } finally {
      if (!silent) {
        setBusy(null);
      }
    }
  };

  const checkDrive = async () => {
    setBusy('check');
    setMessage(null);
    try {
      const token = await ensureAccessToken();
      const file = await findBackupFile(token);
      rememberBackup(file);
      setMessage(file ? 'Dernière sauvegarde Drive retrouvée.' : 'Aucune sauvegarde Drive trouvée pour cette app.');
    } catch (err) {
      setMessage(err instanceof Error ? err.message : 'Impossible de vérifier Google Drive.');
    } finally {
      setBusy(null);
    }
  };

  const restoreFromDrive = async () => {
    setBusy('restore');
    setMessage(null);
    try {
      const token = await ensureAccessToken();
      const file = lastBackup ?? (await findBackupFile(token));
      if (!file) {
        setMessage('Aucune sauvegarde Drive à restaurer.');
        return;
      }
      const remotePayload = await downloadDriveBackup(token, file.id);
      const data = validateImportPayload(remotePayload);
      await restoreBackupPayload(data);
      notifyUpdate();
      setPendingRestore(false);
      rememberBackup(file);
      setMessage(`Restauration Drive terminée: ${backupSummary(data)} restauré(s).`);
    } catch (err) {
      setMessage(storageErrorMessage('Impossible de restaurer depuis Google Drive.', err));
    } finally {
      setBusy(null);
    }
  };

  useEffect(() => {
    if (!accessToken || !autoEnabled) return;

    const handler = () => {
      if (autoTimerRef.current) {
        clearTimeout(autoTimerRef.current);
      }
      autoTimerRef.current = setTimeout(() => {
        saveToDrive(true).catch(() => {
          setAutoEnabled(false);
          setMessage('Sauvegarde automatique Drive mise en pause. Reconnectez Google Drive pour reprendre.');
        });
      }, 6000);
    };

    window.addEventListener('budget-db-update', handler);
    return () => {
      window.removeEventListener('budget-db-update', handler);
      if (autoTimerRef.current) {
        clearTimeout(autoTimerRef.current);
      }
    };
  }, [accessToken, autoEnabled]);

  const disabled = Boolean(busy) || !GOOGLE_CLIENT_ID;

  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-brand-900/70 bg-brand-950/40 p-4">
        <div className="flex flex-col gap-3 min-[420px]:flex-row min-[420px]:items-start min-[420px]:justify-between">
          <div className="min-w-0">
            <p className="text-sm font-semibold text-brand-100">Google Drive privé</p>
            <p className="mt-2 text-sm leading-6 text-brand-200/80">
              Licaio stocke un fichier caché dans le dossier applicatif Drive. Il n’a pas accès au reste de vos fichiers.
            </p>
          </div>
          <span className={`w-fit rounded-full px-3 py-1 text-xs font-semibold ${accessToken ? 'bg-brand-500/15 text-brand-100' : 'bg-slate-800 text-slate-300'}`}>
            {accessToken ? 'Connecté' : 'Non connecté'}
          </span>
        </div>
      </div>

      {!GOOGLE_CLIENT_ID ? (
        <div className="rounded-2xl border border-amber-900/70 bg-amber-950/40 p-4 text-sm leading-6 text-amber-100">
          Ajoutez <span className="font-semibold">NEXT_PUBLIC_GOOGLE_CLIENT_ID</span> dans l’environnement pour activer cette option.
        </div>
      ) : null}

      <div className="rounded-2xl border border-slate-800 bg-slate-950 p-4">
        <p className="text-sm font-medium text-slate-400">Dernière sauvegarde Drive</p>
        <p className="mt-2 text-base font-semibold text-slate-100">{formatBackupTime(lastBackup?.modifiedTime)}</p>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <button type="button" onClick={connectDrive} disabled={disabled} className="secondary-button w-full">
          {busy === 'connect' ? 'Connexion...' : accessToken ? 'Reconnecter Drive' : 'Connecter Drive'}
        </button>
        <button type="button" onClick={() => saveToDrive()} disabled={disabled} className="primary-button w-full">
          {busy === 'save' ? 'Sauvegarde...' : 'Sauvegarder sur Drive'}
        </button>
        <button type="button" onClick={checkDrive} disabled={disabled} className="secondary-button w-full">
          {busy === 'check' ? 'Vérification...' : 'Vérifier Drive'}
        </button>
        <button type="button" onClick={() => setPendingRestore(true)} disabled={disabled || !lastBackup} className="danger-button w-full">
          Restaurer depuis Drive
        </button>
      </div>

      <label className="flex items-start gap-3 rounded-2xl border border-slate-800 bg-slate-950 p-4">
        <input
          type="checkbox"
          checked={autoEnabled}
          onChange={(event) => setAutoEnabled(event.target.checked)}
          disabled={!accessToken}
          className="mt-0.5 h-5 w-5 shrink-0 rounded border-slate-600"
        />
        <span className="min-w-0">
          <span className="block text-sm font-semibold text-slate-100">Sauvegarde automatique pendant la session</span>
          <span className="mt-1 block text-sm leading-6 text-slate-400">
            Après une modification, Licaio attend quelques secondes puis met Drive à jour tant que la session Google reste active.
          </span>
        </span>
      </label>

      {pendingRestore ? (
        <div className="rounded-2xl border border-amber-900/70 bg-amber-950/40 p-4" role="alertdialog" aria-label="Confirmer la restauration Google Drive">
          <p className="text-sm font-semibold text-amber-100">Restaurer la sauvegarde Drive ?</p>
          <p className="mt-2 text-sm leading-6 text-amber-200/80">
            Cette action remplace toutes les données locales actuelles par la sauvegarde Drive.
          </p>
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            <button type="button" onClick={restoreFromDrive} disabled={Boolean(busy)} className="primary-button bg-amber-600 hover:bg-amber-700">
              {busy === 'restore' ? 'Restauration...' : 'Confirmer'}
            </button>
            <button type="button" onClick={() => setPendingRestore(false)} disabled={Boolean(busy)} className="secondary-button">
              Annuler
            </button>
          </div>
        </div>
      ) : null}

      {message ? <p className="rounded-2xl bg-slate-950 p-3 text-sm leading-6 text-slate-300" role="status">{message}</p> : null}
    </div>
  );
}
